import { Dialog, Skeleton, Tooltip } from '@mui/material';
import { IconX } from '@tabler/icons-react';
import PropTypes from 'prop-types';
import { useForm } from '@mantine/form';
import * as yup from 'yup';
import { yupResolver } from '@mantine/form';
import { useScript } from 'usehooks-ts';

import { IconButton, Input, SelectInput, SwitchInput } from 'ui';
import { noop } from 'common/utils';

import CopyBox from './CopyBox';
import { useAccount } from '../../store/account/hooks';
import { getPageUrl, removeFalsyValues } from '../../core/utils';
import ColorInput from '../inputs/ColorInput';
import { DONATION_TYPE_LABELS, DONATION_TYPES } from 'common/constants';

const formValidation = yup.object({
  buttonText: yup.string(),
  embedType: yup.string(),
  type: yup.string(),
  amount: yup.string(),
  styles: yup
    .object({
      backgroundColor: yup.string(),
      color: yup.string(),
      borderRadius: yup.number(),
    })
    .required(),
});

const EMBED_TYPES = [
  { type: 'donate-button', label: 'Donate Button' },
  { type: 'donate-form', label: 'Donation Form' },
];

const EmbedModal = ({ open, onClose, slug }) => {
  const { account } = useAccount();
  const scriptUrl = getPageUrl({ account, slug: 'embeds/donorspring.js' });
  const status = useScript(scriptUrl);

  const form = useForm({
    initialValues: {
      buttonText: '',
      floating: false,
      embedType: EMBED_TYPES[0].type,
      type: DONATION_TYPES.recurring,
      amount: '',
      fullpage: false,
      styles: {
        backgroundColor: '',
        color: '',
        borderRadius: '',
      },
    },
    validate: yupResolver(formValidation),
    validateInputOnChange: true,
  });

  const stringifiedStyles = JSON.stringify(
    removeFalsyValues({
      // When we upgrade to @mantine/form v6, we could use transformValues and getTransformedValues()
      ...form.values.styles,
      borderRadius: form.values.styles.borderRadius ? `${form.values.styles.borderRadius}px` : undefined,
    }),
  );

  const getEmbedString = () => {
    if (form.values.embedType === 'donate-button') {
      return `<donorspring-donate-button${slug ? ` page='${slug}'` : ''}${
        form.values.type === DONATION_TYPES.recurring ? '' : ` type='${DONATION_TYPES.oneTime}'`
      }${form.values.amount ? ` amount='${form.values.amount}'` : ''}${
        stringifiedStyles !== '{}' ? ` styles='${stringifiedStyles}'` : ''
      }${form.values.floating ? ' floating' : ''}${form.values.fullpage ? ' fullpage' : ''}>${
        form.values.buttonText
      }</donorspring-donate-button>`;
    }
    return `<donorspring-donate-form${slug ? ` page='${slug}'` : ''}${
      form.values.type === DONATION_TYPES.recurring ? '' : ` type='${DONATION_TYPES.oneTime}'`
    }${form.values.amount ? ` amount='${form.values.amount}'` : ''}></donorspring-donate-form>`;
  };

  const handleClose = () => {
    onClose();
    form.reset();
  };

  return (
    <Dialog
      maxWidth="md"
      open={open}
      onClose={handleClose}
      sx={{ '.MuiPaper-root': { padding: 0, margin: 0, borderRadius: '12px', width: '100%' } }}
      closeAfterTransition={false}
    >
      <form noValidate className="space-y-6 p-6" onSubmit={noop}>
        <div className="relative flex">
          <div className="flex items-center space-x-3">
            <h3 className="text-h6 font-semibold">Embed</h3>
          </div>

          <IconButton
            Icon={<IconX size={20} className="text-gray-400" />}
            srOnly="Close"
            onClick={handleClose}
            color="transparent"
            className="absolute -right-1 -top-1 !p-1"
          />
        </div>

        <div className="space-y-4">
          <div className="flex items-center space-x-2.5">
            <div className="flex size-[22px] items-center justify-center rounded-full bg-primary-50">
              <span className="text-sm font-semibold text-primary-700">1</span>
            </div>
            <h3 className="text-h6">Install Embed Script</h3>
          </div>
          <p className="text-base">
            Copy and paste the following code snippet into the &lt;head&gt; section of your website. This usually only
            needs to be done one time even if you are using multiple embeds.
          </p>
          <CopyBox pageUrl={`<script async src="${scriptUrl}"></script>`} />
        </div>

        <div className="space-y-4">
          <div className="flex items-center space-x-2.5">
            <div className="flex size-[22px] items-center justify-center rounded-full bg-primary-50">
              <span className="text-sm font-semibold text-primary-700">2</span>
            </div>
            <h3 className="text-h6">Select Embed Type</h3>
          </div>

          <div className="grid grid-cols-[repeat(auto-fill,minmax(262px,262px))] justify-center gap-6">
            {EMBED_TYPES.map(({ type, label }) => (
              <button
                type="button"
                key={type}
                onClick={() => form.setFieldValue('embedType', type)}
                className={`
                  flex 
                  size-[264px] 
                  flex-col 
                  items-center 
                  justify-center
                  space-y-6 
                  rounded-xl 
                  bg-white-100 
                  p-6 
                  text-center 
                  text-base 
                  font-semibold 
                  text-gray-600
                  ring-2
                  ring-offset-1
                  ${
                    form.values.embedType === type
                      ? `
                        text-primary-700 
                        ring-primary-700 
                      `
                      : 'ring-gray-100'
                  }
                `}
              >
                <div className="h-[172px] w-[216px] overflow-hidden border border-gray-50 p-4 text-left">
                  {status === 'ready' && (
                    <div className="pointer-events-none h-full">
                      {type === 'donate-form' ? (
                        <div className="w-[200%] origin-top-left scale-50">
                          <donorspring-donate-form page={slug}></donorspring-donate-form>
                        </div>
                      ) : (
                        <div className="flex h-full items-center justify-center">
                          <donorspring-donate-button page={slug}></donorspring-donate-button>
                        </div>
                      )}
                    </div>
                  )}
                </div>
                <span>{label}</span>
              </button>
            ))}
          </div>
        </div>

        <div className="space-y-4">
          <div className="flex items-center space-x-2.5">
            <div className="flex size-[22px] items-center justify-center rounded-full bg-primary-50">
              <span className="text-sm font-semibold text-primary-700">3</span>
            </div>
            <h3 className="text-h6">Configure Embed Appearance</h3>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-4">
              {form.values.embedType === 'donate-button' && (
                <>
                  <Input
                    id="button-text"
                    label="Button Text"
                    placeholder="Donate Now"
                    helperText={form.errors.buttonText}
                    labelClassName="w-[125px] mr-[5%]"
                    containerClassName={`flex ${form.errors.buttonText ? 'items-start' : 'items-center'}`}
                    inputContainerClassName="flex-1"
                    {...form.getInputProps('buttonText')}
                  />

                  <ColorInput
                    id="backgroundColor"
                    label="Background Color"
                    placeholder="#061C6C"
                    containerClassName="flex items-center"
                    labelClassName="w-[125px] mr-[5%] whitespace-nowrap"
                    inputContainerClassName="flex-1"
                    {...form.getInputProps('styles.backgroundColor')}
                  />

                  <ColorInput
                    id="textColor"
                    label="Text Color"
                    placeholder="#FFFFFF"
                    containerClassName="flex items-center"
                    labelClassName="w-[125px] mr-[5%] whitespace-nowrap"
                    inputContainerClassName="flex-1"
                    {...form.getInputProps('styles.color')}
                  />

                  <Input
                    id="borderRadius"
                    type="number"
                    label="Border Radius"
                    placeholder="8"
                    helperText={form.errors.borderRadius}
                    labelClassName="w-[125px] mr-[5%]"
                    containerClassName={`flex ${form.errors.borderRadius ? 'items-start' : 'items-center'}`}
                    inputContainerClassName="flex-1"
                    min={0}
                    {...form.getInputProps('styles.borderRadius')}
                  />

                  <Tooltip
                    placement="right"
                    title="Buttons will normally appear wherever you place them within your website, like in the header navigation or content. Floating buttons will appear fixed to the bottom right corner of a visitor's screen."
                    arrow
                  >
                    <div>
                      <SwitchInput
                        label="Floating Button"
                        className="w-full flex-row-reverse flex-wrap !content-start !justify-between gap-2"
                        {...form.getInputProps('floating', { type: 'checkbox' })}
                      />
                    </div>
                  </Tooltip>

                  <Tooltip
                    placement="right"
                    title="By default, the popup will only display a donation form. Enabling this option will also show your donation page content including photo, description, and FAQs alongside the donation form similar to your normal donation page."
                    arrow
                  >
                    <div>
                      <SwitchInput
                        label="Show Page Content"
                        className="w-full flex-row-reverse flex-wrap !content-start !justify-between gap-2"
                        {...form.getInputProps('fullpage', { type: 'checkbox' })}
                      />
                    </div>
                  </Tooltip>
                </>
              )}

              <Input
                id="amount"
                type="number"
                label="Default Amount"
                placeholder="50"
                helperText={form.errors.amount}
                labelClassName="w-[125px] mr-[5%]"
                containerClassName={`flex ${form.errors.amount ? 'items-start' : 'items-center'}`}
                inputContainerClassName="flex-1"
                min={1}
                {...form.getInputProps('amount')}
              />

              <SelectInput
                label="Default Type"
                options={[
                  { value: DONATION_TYPES.recurring, label: DONATION_TYPE_LABELS[DONATION_TYPES.recurring] },
                  { value: DONATION_TYPES.oneTime, label: DONATION_TYPE_LABELS[DONATION_TYPES.oneTime] },
                ]}
                helperText={form.errors.type}
                labelClassName="w-[125px] mr-[5%]"
                containerClassName={`flex ${form.errors.type ? 'items-start' : 'items-center'}`}
                {...form.getInputProps('type')}
              />
            </div>

            <div className="-mt-4 flex flex-col">
              <h4 className="text-center text-caption1 font-semibold uppercase text-gray-950">Preview</h4>
              {/* scale-100 allows floating button (position: fixed) relative to this container instead of the whole page */}
              <div className="grow scale-100 border border-gray-50 p-4">
                {status === 'ready' && (
                  <div className="pointer-events-none space-y-0.5">
                    {form.values.embedType === 'donate-button' && (
                      <>
                        <Skeleton variant="text" animation={false} sx={{ fontSize: '1rem' }} />
                        <Skeleton variant="text" animation={false} sx={{ fontSize: '1rem' }} />
                        <Skeleton variant="text" animation={false} sx={{ fontSize: '1rem', width: '85%' }} />
                        <donorspring-donate-button
                          styles={stringifiedStyles}
                          floating={form.values.floating ? true : undefined}
                          page={slug}
                          type={form.values.type === DONATION_TYPES.recurring ? undefined : 'one-time'}
                          amount={form.values.amount || undefined}
                        >
                          {form.values.buttonText}
                        </donorspring-donate-button>
                        <Skeleton variant="text" animation={false} sx={{ fontSize: '1rem' }} />
                        <Skeleton variant="text" animation={false} sx={{ fontSize: '1rem' }} />
                        <Skeleton variant="text" animation={false} sx={{ fontSize: '1rem', width: '65%' }} />
                      </>
                    )}
                    {form.values.embedType === 'donate-form' && (
                      <donorspring-donate-form
                        page={slug}
                        type={form.values.type === DONATION_TYPES.recurring ? undefined : 'one-time'}
                        amount={form.values.amount || undefined}
                      ></donorspring-donate-form>
                    )}
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>

        <div className="space-y-4">
          <div className="flex items-center space-x-2.5">
            <div className="flex size-[22px] items-center justify-center rounded-full bg-primary-50">
              <span className="text-sm font-semibold text-primary-700">4</span>
            </div>
            <h3 className="text-h6">Install Embed Code</h3>
          </div>

          <p className="text-base">
            Copy and paste the following code snippet into your website where you want the embed to appear.
          </p>

          <CopyBox pageUrl={getEmbedString()} />
        </div>
      </form>
    </Dialog>
  );
};

EmbedModal.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  slug: PropTypes.string,
};

export default EmbedModal;
